import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ManualEditsTable } from "./ManualEditsTable";
import { ManualEditsProfileForm } from "./ManualEditsProfileForm";
import { fetchUsersByRoles, setManualEditsFilters } from "../../../features/admin/adminSlice";
import { TeamDropdown } from "../../../components/common/TeamDropdown";
import teamService from "../../../services/teamService";
import { SegmentedToggle } from "../../../components/common/SegmentedToggle";
import { ManualEditsTeams } from "./ManualEditsTeams";
const TEAMS_PER_PAGE = 15;

const TABS = [
  { value: "players", label: "Player Profiles" },
  { value: "teams", label: "Team Profiles" },
];

export const ManualEditsPage = () => {
  const dispatch = useDispatch();
  const {
    usersByRole,
    manualEditsFilters,
    manualEditsPagination,
    manualEditsLoading
  } = useSelector((state) => state.admin);
  const [activeTab, setActiveTab] = useState("players");
  const [selectedUser, setSelectedUser] = useState(null);

  // Team dropdown state
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [teamsLoadingMore, setTeamsLoadingMore] = useState(false);
  const [teamsHasMore, setTeamsHasMore] = useState(false);
  const teamsPageRef = useRef(1);
  const teamsSearchRef = useRef("");

  // Fetch teams with pagination
  const fetchTeams = useCallback(async (page = 1, search = "", append = false, isInitial = false) => {
    if (isInitial) {
      setTeamsLoading(true);
    } else if (page > 1) {
      setTeamsLoadingMore(true);
    }
    try {
      const params = { page, limit: TEAMS_PER_PAGE };
      if (search) params.teamName = search;
      const data = await teamService.getPlayerTeams(params);
      const teamsList = data?.teams || data || [];
      const pagination = data?.pagination || {};
      const totalPages = pagination.totalPages || data?.totalPages || 1;
      const currentPage = pagination.currentPage || page;

      if (append) {
        setTeams((prev) => [...prev, ...teamsList]);
      } else {
        setTeams(teamsList);
      }
      teamsPageRef.current = currentPage;
      setTeamsHasMore(currentPage < totalPages);
    } catch (err) {
      console.error("Failed to fetch teams:", err);
    } finally {
      setTeamsLoading(false);
      setTeamsLoadingMore(false);
    }
  }, []);

  // Initial team load
  useEffect(() => {
    fetchTeams(1, "", false, true);
  }, [fetchTeams]);

  const handleTeamSearch = useCallback((searchTerm) => {
    teamsSearchRef.current = searchTerm;
    teamsPageRef.current = 1;
    fetchTeams(1, searchTerm, false);
  }, [fetchTeams]);

  const handleTeamLoadMore = useCallback(() => {
    const nextPage = teamsPageRef.current + 1;
    fetchTeams(nextPage, teamsSearchRef.current, true);
  }, [fetchTeams]);

  useEffect(() => {
    // Reset to page 1 on mount with player role
    dispatch(setManualEditsFilters({ page: 1, limit: 20, role: 'player' }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchUsersByRoles(manualEditsFilters));
  }, [dispatch, manualEditsFilters]);

  const handleRoleChange = (role) => {
    dispatch(setManualEditsFilters({ page: 1, role }));
  };

  const handleSearchChange = (search) => {
    dispatch(setManualEditsFilters({ page: 1, search }));
  };

  const handleTeamFilterChange = (team) => {
    if (team) {
      setSelectedTeam(team);
      const teamId = team._id || team.id || "";
      dispatch(setManualEditsFilters({ page: 1, teamId }));
    } else {
      setSelectedTeam(null);
      dispatch(setManualEditsFilters({ page: 1, teamId: undefined }));
    }
  };

  // Map users to row format
  const rows = usersByRole.map((user) => ({
    id: user._id,
    name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'N/A',
    role: user.role.charAt(0).toUpperCase() + user.role.slice(1),
    lastUpdate: new Date(user.updatedAt).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }),
    status: user.registrationStatus.charAt(0).toUpperCase() + user.registrationStatus.slice(1),
    commitmentStatus: user.commitmentStatus === 'committed' && user.committedTo
      ? `Committed "${user.committedTo}"`
      : user.commitmentStatus === 'committed'
      ? 'Committed'
      : 'Uncommitted',
    teamName: user.team?.name || user.school || '-',
    teamLogo: user.team?.logo || null,
    ...user,
  }));

  const handleRowClick = (row) => {
    setSelectedUser(row);
  };

  const handleLoadMore = () => {
    const nextPage = manualEditsPagination.currentPage + 1;
    dispatch(setManualEditsFilters({ page: nextPage }));
  };

  return (
    <div className="rounded-3xl bg-jp-card border border-jp-line shadow-sm p-6 h-full">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-4">
          <h2 className="text-jp-accent-ink text-sm font-bold uppercase tracking-widest text-left md:text-left">
            MANUAL EDITS
          </h2>
          <SegmentedToggle options={TABS} value={activeTab} onChange={setActiveTab} ariaLabel="Profiles to edit" />
        </div>
        {activeTab === "players" && (
        <div className="flex items-center gap-3 text-sm text-jp-ink">
          <div className="flex items-center gap-2 h-8">
            <span>Team:</span>
            <TeamDropdown
              selectedTeam={selectedTeam}
              onSelectTeam={handleTeamFilterChange}
              teams={teams}
              loading={teamsLoading}
              placeholder="All Teams"
              variant="admin"
              onSearch={handleTeamSearch}
              onLoadMore={handleTeamLoadMore}
              hasMore={teamsHasMore}
              loadingMore={teamsLoadingMore}
            />
          </div>
        </div>
        )}
      </div>

      {activeTab === "teams" ? (
        <ManualEditsTeams />
      ) : (
      <div className="grid grid-cols-1 xl:grid-cols-[1.6fr,1.2fr] gap-6 h-[calc(100%-2rem)]">
        <ManualEditsTable
          rows={rows}
          onRowClick={handleRowClick}
          loading={manualEditsLoading}
          pagination={manualEditsPagination}
          onLoadMore={handleLoadMore}
          roleFilter={manualEditsFilters.role}
          onRoleChange={handleRoleChange}
          onSearchChange={handleSearchChange}
        />
        <ManualEditsProfileForm
          userData={selectedUser}
        />
      </div>
      )}
    </div>
  );
};
